import React, { useState } from 'react';

import { type AutoPlacementOptions } from '@floating-ui/core';
import {
    autoPlacement,
    autoUpdate,
    FloatingFocusManager,
    offset,
    type Placement,
    useClick,
    useDismiss,
    useFloating,
    useInteractions,
} from '@floating-ui/react';
import { AnimatePresence, type HTMLMotionProps } from 'framer-motion';

import { EmojiPicker, Portal, PortalArea } from '@pkg/components';
import { type EmojiPickerProps } from '@pkg/components/EmojiPicker';
import { type OutsideClickExclusion } from '@pkg/hooks';

import { ReactComponent as SmileyIcon } from './smiley.svg';

import { Action, EmojiPopover, IconWrapper, StyledEmojiPickerCombo } from './styles';

export interface EmojiPickerComboFloatingProps {
    closeExclusions?: OutsideClickExclusion;
    onSelectEmoji: EmojiPickerProps['onSelectEmoji'];
    // This prop is utilized to retrieve internal state (`show` vs `hidden`).
    onPopoverAnimate?: HTMLMotionProps<'div'>['onAnimationComplete'];
    placement?: Placement;
    autoPlacementOptions?: Partial<AutoPlacementOptions>;
}

export function EmojiPickerComboFloating({
    // closeExclusions,
    onSelectEmoji,
    onPopoverAnimate,
    placement = 'right-start',
    autoPlacementOptions,
}: EmojiPickerComboFloatingProps) {
    const [popoverOpen, setPopoverOpen] = useState(false);
    const {
        x,
        y,
        placement: currentPlacement,
        context,
        refs,
        strategy,
    } = useFloating({
        open: popoverOpen,
        onOpenChange: setPopoverOpen,
        middleware: [offset(20), autoPlacement(autoPlacementOptions)],
        whileElementsMounted: autoUpdate,
        placement,
    });

    const dismiss = useDismiss(context, {
        outsidePressEvent: 'mousedown',
    });

    const click = useClick(context);

    const { getFloatingProps, getReferenceProps } = useInteractions([click, dismiss]);

    const emojiPickerMarkup = popoverOpen ? (
        <EmojiPopover
            ref={refs.setFloating}
            key="EmojiPickerCombo-Popover"
            onAnimationComplete={onPopoverAnimate}
            style={{
                position: strategy,
                top: y ?? 0,
                left: x ?? 0,
            }}
            custom={{ placement: currentPlacement }}
            {...getFloatingProps()}
        >
            <FloatingFocusManager context={context} modal={false}>
                <EmojiPicker darkMode onSelectEmoji={onSelectEmoji} />
            </FloatingFocusManager>
        </EmojiPopover>
    ) : null;

    return (
        <StyledEmojiPickerCombo>
            <Action ref={refs.setReference} aria-pressed={popoverOpen} {...getReferenceProps()}>
                <IconWrapper>
                    <SmileyIcon />
                </IconWrapper>
            </Action>

            <Portal destination={PortalArea.Transition}>
                <AnimatePresence>{emojiPickerMarkup}</AnimatePresence>
            </Portal>
        </StyledEmojiPickerCombo>
    );
}
